/**
 * Format module name for display: uppercase and replace underscores with spaces
 * e.g., "crawl_hints" -> "CRAWL HINTS", "dns" -> "DNS"
 */
export function formatModuleName(module: string): string {
  return module.replace(/_/g, " ").toUpperCase();
}

/**
 * Validate domain name syntax.
 * Returns true if the domain has valid syntax, false otherwise.
 *
 * Valid domains must:
 * - Have at least one dot (e.g., "example.com" not "example")
 * - Not start or end with a dot or hyphen
 * - Only contain alphanumeric characters, dots, and hyphens
 * - Have valid label lengths (1-63 chars each, total max 253)
 */
export function isValidDomain(domain: string): boolean {
  if (!domain || typeof domain !== "string") return false;

  // Trim whitespace
  const trimmed = domain.trim().toLowerCase();

  // Must have at least one dot
  if (!trimmed.includes(".")) return false;

  // Total length check
  if (trimmed.length > 253) return false;

  // Domain regex: valid hostname pattern
  // Each label: starts with alphanumeric, can contain hyphens, ends with alphanumeric
  // Labels separated by dots, TLD must be at least 2 chars
  const domainRegex = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

  // Fallback for environments without lookbehind support
  try {
    if (!domainRegex.test(trimmed)) return false;
  } catch {
    // Simpler regex without lookbehind for older browsers
    const simpleDomainRegex = /^[a-z0-9][a-z0-9-]*[a-z0-9](\.[a-z0-9][a-z0-9-]*[a-z0-9])*\.[a-z]{2,}$/;
    const singleCharLabelRegex = /^[a-z0-9](\.[a-z0-9][a-z0-9-]*[a-z0-9])*\.[a-z]{2,}$/;
    if (!simpleDomainRegex.test(trimmed) && !singleCharLabelRegex.test(trimmed)) return false;
  }

  // Check each label length
  const labels = trimmed.split(".");
  for (const label of labels) {
    if (label.length === 0 || label.length > 63) return false;
    if (label.startsWith("-") || label.endsWith("-")) return false;
  }

  // TLD must be at least 2 characters and only letters
  const tld = labels[labels.length - 1];
  if (tld.length < 2 || !/^[a-z]+$/.test(tld)) return false;

  return true;
}
